import React, { ChangeEvent, useMemo } from "react";
import { Checkbox } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { GetInputPropsReturnType } from "@mantine/form/lib/types";
import styles from "./FormCheckboxInput.module.css";

interface FormCheckboxInputProps {
  disabled?: boolean;
  label: string;
  value?: string; // "SI" | "NO"
  setValue?: (value: string) => void;
  formInfo?: {
    formLabel: string;
    formContext: () => UseFormReturnType<any>;
  };
}

/**
 * Checkbox input component integrated with Mantine Form context.
 * Works in 3 modes:
 *   – Fully controlled via `value` + `setValue`
 *   – Connected to Mantine form via `formInfo`
 *   – Uncontrolled fallback (internal state inside Mantine Checkbox)
 * In all cases the rendered checkbox is driven by a **boolean** `checked` prop so
 * that the native `checked`/`data-checked` attributes stay in sync at the first click.
 */
export default function FormCheckboxInput(props: FormCheckboxInputProps) {
  // Retrieve form, if any
  const form = props.formInfo ? props.formInfo.formContext() : undefined;
  const formLabel = props.formInfo?.formLabel ?? props.label;
  // In this codebase, editable fields are nested under the "current" key
  const formPath = `current.${formLabel}`;
  
  // Get input props from form (similar to FormTextInput)
  // Use formLabel (not formPath) to match FormTextInput pattern
  const inputProps = form ? form.getInputProps(formLabel) : {};
  
  // Extract only the props we need from form, avoiding conflicts with our custom handlers
  const { onChange: formOnChange, value: formValue, ...safeInputProps } = inputProps as any;

  // Derive current "checked" state from value hierarchy (props -> form -> NO)
  const checked = useMemo(() => {
    if (props.value !== undefined) return String(props.value).toUpperCase() === "SI";
    if (form && props.formInfo) {
      // Use the form's value from inputProps first (for active form state)
      let formVal = formValue;
      
      // If no form value, check the nested current structure for initial values
      if (formVal === undefined || formVal === null) {
        formVal = form.getValues().current?.[formLabel];
      }
      
      return String(formVal ?? "NO").toUpperCase() === "SI";
    }
    return false;
  }, [props.value, formValue, form?.values, formLabel]);

  // Change handler – always writes boolean to form and string to callbacks
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;
    const newValue = isChecked ? "SI" : "NO";

    // external callback (string "SI/NO")
    props.setValue?.(newValue);

    // Use form's own onChange handler (similar to FormTextInput)
    // This properly handles dirty state tracking for nested fields
    if (form && formOnChange) {
      formOnChange(newValue);
    }
  };

  return (
    <Checkbox
      label={props.label}
      checked={checked}
      onChange={handleChange}
      size="sm"
      disabled={props.disabled ?? false}
      radius="sm"
      name={formLabel}
      classNames={{
        root: styles.checkboxRoot,
        input: styles.checkboxInput,
        icon: styles.checkboxIcon,
        label: styles.checkboxLabel,
        inner: styles.checkboxInner,
      }}
      {...safeInputProps}
    />
  );
}
